<template>
  <div>
    <v-col cols="12">
      <v-card-title class="mb-n6">
        {{ $t("components.profile.EditProfileDialog.general") }}
      </v-card-title>
    </v-col>

    <!-- Section 1: First Name, Last name of User, username -->
    <template v-if="displayOptions.isGeneralEnabled">
      <v-card-text>
        <v-col cols="12" v-if="showUserNameField">
          <v-text-field
            class="mb-n6"
            outlined
            :label="$t('components.profile.ProfileCard.userName')"
            disabled
            v-model="user.userName"
            data-test-profile-userName
          />
        </v-col>
        <v-row class="mt-n4">
          <v-col cols="6" md="4">
            <v-radio-group v-model="user.isCompany" row :disabled="disabled">
              <v-radio class="ml-3" :label="$t('components.profile.EditProfileDialog.private')" :value="false" />
            </v-radio-group>
          </v-col>
          <v-col cols="6" md="8">
            <v-radio-group v-model="user.isCompany" row :disabled="disabled">
              <v-radio
                :label="$t('components.profile.EditProfileDialog.company')"
                :value="true"
                data-test-form-iscompany
              />
            </v-radio-group>
          </v-col>
        </v-row>
        <v-col cols="12">
          <v-text-field
            class="mb-n6"
            outlined
            :label="$t('components.profile.EditProfileDialog.name')"
            :rules="[(v) => !!v || $t('components.profile.EditProfileDialog.mandatoryInput')]"
            v-model="user.firstName"
            data-test-profile-firstName
            :disabled="disabled"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            class="mb-n6"
            outlined
            :label="$t('components.profile.EditProfileDialog.lastname')"
            :rules="[(v) => !!v || $t('components.profile.EditProfileDialog.mandatoryInput')]"
            v-model="user.lastName"
            data-test-profile-lastName
            :disabled="disabled"
          />
        </v-col>
      </v-card-text>
    </template>
    <!-- Section 2: Company name + tax info(if Corporate client is selected) -->
    <template v-if="displayOptions.isCompanyEnabled">
      <div v-if="user.isCompany">
        <v-card-text>
          <v-col cols="12">
            <v-text-field
              :rules="[(v) => !!v || $t('components.profile.EditProfileDialog.mandatoryInput')]"
              class="mb-n6 mt-n7"
              outlined
              :label="$t('components.profile.EditProfileDialog.companyName') + ' *'"
              v-model="user.company"
              data-test-profile-company
              :disabled="disabled"
            />
          </v-col>
        </v-card-text>

        <!-- Tax number header + info -->
        <v-col cols="12">
          <v-card-title>
            {{ $t("components.profile.EditProfileDialog.taxInformation") }}
          </v-card-title>
          <v-card-subtitle v-if="isTaxDeductibleEnabled">
            {{ $t("components.profile.EditProfileDialog.taxInformationQuestion") }}
          </v-card-subtitle>
        </v-col>

        <!-- Tax deductible radio buttons -->
        <v-card-text v-if="isTaxDeductibleEnabled">
          <v-row class="mt-n13 mb-n7">
            <v-col cols="6" md="4">
              <v-radio-group v-model="user.isTaxDeductible" row :disabled="disabled">
                <v-radio class="ml-3" :label="$t('components.profile.EditProfileDialog.yes')" :value="true" />
              </v-radio-group>
            </v-col>
            <v-col cols="6" md="8">
              <v-radio-group v-model="user.isTaxDeductible" row :disabled="disabled">
                <v-radio :label="$t('components.profile.EditProfileDialog.no')" :value="false" />
              </v-radio-group>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Tax deductible required text -->
        <v-col cols="12" v-if="isTHGContext">
          <v-card-subtitle class="mt-n6 mb-n3" v-if="user.isTaxDeductible">
            {{ $t("components.profile.EditProfileDialog.taxNumberRequiredText") }}
          </v-card-subtitle>
          <v-card-subtitle class="mt-n6 mb-n3" v-else>
            {{ $t("components.profile.EditProfileDialog.taxNumberNotRequiredText") }}
          </v-card-subtitle>
        </v-col>

        <!-- Tax number -->
        <div v-if="user.isTaxDeductible">
          <v-card-text v-if="isResubmitTaxnumberEnabled">
            <v-row class="mt-n12 mb-n8">
              <v-col cols="6" md="4">
                <v-radio-group v-model="localIsResubmitTaxnumber" row>
                  <v-radio
                    class="ml-3"
                    :label="$t('report.thgContactData.register')"
                    :value="false"
                    data-test-form-isNotResubmittingTaxnumber
                  />
                </v-radio-group>
              </v-col>
              <v-col cols="6" md="8">
                <v-radio-group v-model="localIsResubmitTaxnumber" row>
                  <v-radio
                    :label="$t('report.thgContactData.resubmit')"
                    :value="true"
                    data-test-form-isResubmittingTaxnumber
                  />
                </v-radio-group>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-text v-if="!localIsResubmitTaxnumber">
            <v-col cols="12">
              <v-text-field
                class="mt-n4 mb-n4"
                outlined
                :rules="taxNumberRules"
                :label="`${$t('components.profile.EditProfileDialog.taxnumber')} ${isTaxnumberRequired ? '*' : ''}`"
                v-model="user.taxnumber"
                data-test-profile-taxnumber
                :disabled="disabled"
              />
            </v-col>
          </v-card-text>
        </div>
      </div>
    </template>
    <!-- Section 3: Contact info -->
    <template v-if="displayOptions.isContactEnabled">
      <v-col cols="12">
        <v-card-title>
          {{ $t("components.profile.EditProfileDialog.contactInformation") }}
        </v-card-title>
        <v-card-subtitle class="mb-n6" v-if="isTHGContext && !hideContactInformationText">
          {{ $t("components.profile.EditProfileDialog.contactInformationText") }}
        </v-card-subtitle>
      </v-col>

      <!-- Section 3: Contact info -->
      <v-card-text>
        <v-col cols="12">
          <v-text-field
            class="mb-n6"
            outlined
            :label="$t('components.profile.EditProfileDialog.email')"
            :rules="emailRules"
            v-model="user.contact.email"
            type="email"
            data-test-profile-email
            :disabled="disabled"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            class="mb-n6"
            outlined
            :label="phoneLabel"
            :rules="phoneNumberRules"
            v-model="user.contact.phone"
            data-test-profile-phone
            :disabled="disabled"
          />
        </v-col>
      </v-card-text>
    </template>
    <!-- Section 4: Address -->
    <template v-if="displayOptions.isAddressEnabled">
      <v-col cols="12">
        <v-card-title> {{ $t("components.profile.EditProfileDialog.adress") }} </v-card-title>
        <v-card-subtitle class="mb-n6" v-if="user.isCompany && isTHGContext">
          {{ $t("components.profile.EditProfileDialog.adressTextCompany") }}
        </v-card-subtitle>
        <v-card-subtitle class="mb-n6" v-else-if="isTHGContext">
          {{ $t("components.profile.EditProfileDialog.adressText") }}
        </v-card-subtitle>
      </v-col>

      <!-- Section 4: Address -->
      <v-card-text>
        <v-row class="mt-n4 mb-n11" v-if="isResubmitAddressEnabled">
          <v-col cols="6" md="4">
            <v-radio-group v-model="localIsResubmitAddress" row>
              <v-radio
                class="ml-3"
                :label="$t('report.thgContactData.register')"
                :value="false"
                data-test-form-isNotResubmittingAddressDetails
              />
            </v-radio-group>
          </v-col>
          <v-col cols="6" md="8">
            <v-radio-group v-model="localIsResubmitAddress" row>
              <v-radio
                :label="$t('report.thgContactData.resubmit')"
                :value="true"
                data-test-form-isResubmittingAddressDetails
              />
            </v-radio-group>
          </v-col>
        </v-row>
      </v-card-text>
      <!-- Section 4: Address -->
      <!-- Don't allow address to be resubmitted, so show the fields -->
      <v-card-text v-if="!localIsResubmitAddress">
        <v-col cols="12">
          <v-text-field
            class="mb-n6"
            outlined
            :rules="addressRules"
            :label="`${$t('components.profile.EditProfileDialog.street')} ${isAddressRequired ? '*' : ''}`"
            v-model="user.address.street"
            data-test-profile-street
            :disabled="disabled"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            class="mb-n6"
            outlined
            :rules="addressRules"
            :label="`${$t('components.profile.EditProfileDialog.zip')} ${isAddressRequired ? '*' : ''}`"
            v-model="user.address.zip"
            data-test-profile-zip
            :disabled="disabled"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            class="mb-n6"
            outlined
            :rules="addressRules"
            :label="`${$t('components.profile.EditProfileDialog.city')} ${isAddressRequired ? '*' : ''}`"
            v-model="user.address.city"
            data-test-profile-city
            :disabled="disabled"
          />
        </v-col>
        <v-col cols="12" v-if="!hideState">
          <v-text-field
            class="mb-n6"
            outlined
            :rules="stateRules"
            :label="`${$t('components.profile.EditProfileDialog.state')} ${isAddressRequired ? '*' : ''}`"
            v-model="user.address.state"
            data-test-profile-state
            :disabled="disabled"
          />
        </v-col>
        <v-col cols="12" v-if="!hideCountry">
          <v-autocomplete
            class="mb-n6"
            outlined
            :rules="addressRules"
            :label="`${$t('components.profile.EditProfileDialog.country')} ${isAddressRequired ? '*' : ''}`"
            v-model="user.address.countryCode"
            data-test-profile-countryCode
            :disabled="disabled"
            :items="countryCodes"
          />
        </v-col>
      </v-card-text>
    </template>
  </div>
</template>

<script lang="ts">
import { autoCompleteCountryCodes } from "@/lib/CountryCodeHelper";
import { emailRule, extendetPhoneRule, disallowUmlaut } from "@/lib/rules/contactRule";
import { requiredRule } from "@/lib/rules/requiredRule";
import DarkModeHighlightMixin from "@/mixins/DarkModeHighlightMixin.vue";
import { IUser } from "@/models/user.entity";
import { Component, Prop } from "vue-property-decorator";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { ConfigModule } from "@/store/modules/config";

/**
 * Interface which regulates the visibility of the 4 sections in the profile
 * General(names, company name, type of user)
 * Company(tax number and deduction)
 * Contact(email, phone)
 * Address(full address)
 */
export interface ProfileFormFieldsDisplayOptions {
  isGeneralEnabled: boolean;
  isCompanyEnabled: boolean;
  isContactEnabled: boolean;
  isAddressEnabled: boolean;
}

@Component({
  components: {}
})
export default class ProfileFormFields extends DarkModeHighlightMixin {
  @Prop({ default: true })
  showUserNameField!: boolean;

  @Prop()
  user!: IUser;

  @Prop()
  disabled!: boolean;

  @Prop({ default: false })
  isResubmitAddress!: boolean;

  @Prop({ default: false })
  isResubmitTaxnumber!: boolean;

  @Prop({ default: false })
  isTaxnumberRequired!: boolean;

  /**
   * If true, the phone number field is required.
   */
  @Prop({ default: false })
  isPhoneRequired!: boolean;

  /**
   * If true, the THG contact information subtitle is hidden (e.g. because the partner configured custom data protection texts).
   */
  @Prop({ default: false })
  hideContactInformationText!: boolean;

  @Prop({ default: true })
  isTaxDeductibleEnabled!: boolean;

  @Prop({ default: false })
  isResubmitAddressEnabled!: boolean;

  @Prop({ default: false })
  isAddressRequired!: boolean;

  @Prop({ default: false })
  isResubmitTaxnumberEnabled!: boolean;

  @Prop({ default: false })
  hideState!: boolean;

  @Prop({ default: false })
  hideCountry!: boolean;

  /**
   * Object to regulate which parts of the fields are shown(Sections)
   * We have 4 sections: General, Company, Contact and Address.
   * By default all are shown.
   */

  @Prop({
    default: () => ({ isGeneralEnabled: true, isCompanyEnabled: true, isContactEnabled: true, isAddressEnabled: true })
  })
  displayOptions!: ProfileFormFieldsDisplayOptions;

  get isTHGContext() {
    return ConfigModule.appContext === AppContextEnum.THG_PORTAL || ConfigModule.appContext === AppContextEnum.THG;
  }

  get localIsResubmitAddress() {
    return this.isResubmitAddress;
  }
  set localIsResubmitAddress(isResubmitAddress: boolean) {
    this.$emit("update:isResubmitAddress", isResubmitAddress);
  }

  get localIsResubmitTaxnumber() {
    return this.isResubmitTaxnumber;
  }
  set localIsResubmitTaxnumber(isResubmitTaxnumber: boolean) {
    this.$emit("update:isResubmitTaxnumber", isResubmitTaxnumber);
  }

  readonly countryCodes = autoCompleteCountryCodes;

  get emailRules() {
    const rules = [];

    rules.push(requiredRule());
    rules.push(emailRule());
    rules.push(disallowUmlaut());

    return rules;
  }

  /**
   * Shows the phone label with a star when required and the optional variant otherwise.
   */
  get phoneLabel(): string {
    return this.isPhoneRequired
      ? `${this.$t("common.nouns.phone")} *`
      : String(this.$t("components.profile.EditProfileDialog.phone"));
  }

  get phoneNumberRules() {
    const rules = [];

    if (this.isPhoneRequired) {
      rules.push(requiredRule());
    }
    rules.push(extendetPhoneRule());

    return rules;
  }

  get addressRules() {
    return this.isAddressRequired ? [requiredRule()] : [];
  }

  /**
   * Should we require state. For now not at all.
   * Expose a input to regulate this functionality.
   */
  get stateRules() {
    return [];
  }

  get taxNumberRules() {
    return this.isTaxnumberRequired && this.user.isCompany ? [requiredRule()] : [];
  }
}
</script>
